/**
 * Barycenter Ordering
 * 
 * Orders nodes within layers using the barycenter (center of gravity) method.
 * Nodes are positioned based on the average position of their connected nodes.
 */

import { groupBySharedDescendants, orderGroupsByDescendantPosition } from './descendantGrouping';

/**
 * Order nodes within a single group by barycenter
 * @param {Array} group - Array of node IDs in this group
 * @param {Map} adjacentPositions - Map of adjacent layer node IDs to positions
 * @param {Object} graph - Dependency graph
 * @param {string} connectionType - 'parents' or 'children'
 * @param {Map} taskLayers - Map of nodeId -> layer index
 * @param {number} targetLayerIndex - Layer index of adjacent layer
 * @param {number} groupStartIndex - Starting position index for this group
 * @returns {Array} Ordered array of node IDs
 */
export function orderGroupByBarycenter(group, adjacentPositions, graph, connectionType, taskLayers, targetLayerIndex, groupStartIndex) {
  const barycenters = group.map((nodeId, idx) => {
    const connections = graph[connectionType].get(nodeId) || [];
    
    const validConnections = connections.filter(id => {
      if (!adjacentPositions.has(id)) return false;
      const connLayer = taskLayers.get(id);
      return connLayer === targetLayerIndex;
    });
    
    if (validConnections.length === 0) {
      // No direct connections in the primary direction — fall back to the
      // OPPOSITE direction (children when ordering by parents, or vice-versa).
      // This handles pulled-down nodes that have no parents in the adjacent
      // layer but DO have children below: we use the children's positions so
      // the node is placed on the side that minimises edge crossings.
      const oppositeType = connectionType === 'parents' ? 'children' : 'parents';
      const oppositeConnections = graph[oppositeType].get(nodeId) || [];
      
      if (oppositeConnections.length > 0) {
        // Build a position map from ALL layers for the opposite connections
        // We only need a relative ordering hint, so use taskLayers as proxy
        const oppositePositioned = oppositeConnections.filter(id => adjacentPositions.has(id));
        
        if (oppositePositioned.length > 0) {
          const sum = oppositePositioned.reduce((acc, id) => acc + adjacentPositions.get(id), 0);
          return { nodeId, barycenter: sum / oppositePositioned.length, hasConnections: true };
        }
      }
      
      // Truly no connections in either direction — use original position
      return { nodeId, barycenter: groupStartIndex + idx, hasConnections: false };
    }
    
    const sum = validConnections.reduce((acc, id) => acc + adjacentPositions.get(id), 0);
    return { nodeId, barycenter: sum / validConnections.length, hasConnections: true };
  });
  
  // Sort by barycenter, with tie-breaking by node ID for determinism
  barycenters.sort((a, b) => {
    if (a.hasConnections && b.hasConnections) {
      const diff = a.barycenter - b.barycenter;
      if (Math.abs(diff) < 0.001) return (a.nodeId || '').localeCompare(b.nodeId || '');
      return diff;
    }
    if (!a.hasConnections && !b.hasConnections) {
      const diff = a.barycenter - b.barycenter;
      if (Math.abs(diff) < 0.001) return (a.nodeId || '').localeCompare(b.nodeId || '');
      return diff;
    }
    if (a.hasConnections) return -1;
    return 1;
  });
  
  return barycenters.map(item => item.nodeId);
}

/**
 * Order a layer's nodes by barycenter (average position of connected nodes)
 * ENHANCED: Respects shared-descendant groups - nodes in the same group stay together
 * 
 * @param {Array} layer - Array of node IDs in this layer
 * @param {Array} adjacentLayer - Adjacent layer for reference
 * @param {Object} graph - Dependency graph
 * @param {string} connectionType - 'parents' or 'children'
 * @param {Map} taskLayers - Map of nodeId -> layer index
 * @param {number} targetLayerIndex - Layer index of adjacent layer
 * @param {number} layerIndex - Index of current layer
 * @returns {Array} Ordered array of node IDs
 */
export function orderLayerByBarycenter(layer, adjacentLayer, graph, connectionType, taskLayers, targetLayerIndex, layerIndex) {
  const adjacentPositions = new Map();
  adjacentLayer.forEach((nodeId, index) => {
    adjacentPositions.set(nodeId, index);
  });
  
  // Step 1: Group nodes by shared descendants
  const groups = groupBySharedDescendants(layer, graph, taskLayers);
  
  // Step 2: Order groups based on their descendants' positions
  // For backward pass (connectionType='children'), use the adjacent layer (below)
  // For forward pass (connectionType='parents'), groups are already in good order from previous backward pass
  const orderedGroups = connectionType === 'children' 
    ? orderGroupsByDescendantPosition(groups, graph, adjacentLayer, taskLayers, layerIndex)
    : groups;
  
  // Step 3: Order nodes within each group by barycenter
  const result = [];
  let groupStartIndex = 0;
  
  for (const group of orderedGroups) {
    const orderedGroup = orderGroupByBarycenter(
      group, 
      adjacentPositions, 
      graph, 
      connectionType, 
      taskLayers, 
      targetLayerIndex,
      groupStartIndex
    );
    result.push(...orderedGroup);
    groupStartIndex += group.length;
  }
  
  return result;
}
